import moment from 'moment';
import { Workout } from '../models/Workout';
import { WorkoutOefeningKort } from '../models/WorkoutOefeningKort';

export type ProgramStatus = 'Actief' | 'Toekomst' | 'Afgerond' | 'In bewerking';

export function isTrainingDayFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

export function hasConfiguredTrainingDays(workout: Workout): boolean {
  return [
    workout.Maandag,
    workout.Dinsdag,
    workout.Woensdag,
    workout.Donderdag,
    workout.Vrijdag,
    workout.Zaterdag,
    workout.Zondag,
  ].some(isTrainingDayFlag);
}

export function countTrainingDaysPerWeek(workout: Workout): number {
  return [
    workout.Maandag,
    workout.Dinsdag,
    workout.Woensdag,
    workout.Donderdag,
    workout.Vrijdag,
    workout.Zaterdag,
    workout.Zondag,
  ].filter(isTrainingDayFlag).length;
}

export function getProgramStatus(workout: Workout, referenceDate: Date = new Date()): ProgramStatus {
  if (!hasConfiguredTrainingDays(workout) && workout.Duur_in_weken === 0) {
    return 'In bewerking';
  }

  if (!workout?.Startdatum || workout.Duur_in_weken === 0) {
    return 'Afgerond';
  }

  const reference = moment(referenceDate).startOf('day');
  const programStart = moment(workout.Startdatum).startOf('day');
  const programEnd = programStart.clone().add(workout.Duur_in_weken * 7, 'days');

  if (reference.isBefore(programStart, 'day')) {
    return 'Toekomst';
  }
  if (!reference.isBefore(programEnd, 'day')) {
    return 'Afgerond';
  }
  return 'Actief';
}

export function dedupeWorkoutsById(workouts: Workout[]): Workout[] {
  const seen = new Set<string>();
  return (workouts ?? []).filter((workout) => {
    if (!workout?.Id || seen.has(workout.Id)) {
      return false;
    }
    seen.add(workout.Id);
    return true;
  });
}

export function isWorkoutPlannedOnDate(workout: Workout, datum: Date): boolean {
  return getProgramStatus(workout, datum) === 'Actief' && isWorkoutScheduledOnDate(workout, datum);
}

export function isWorkoutScheduledOnDate(workout: Workout, datum: Date): boolean {
  if (!workout?.Startdatum || !workout.Duur_in_weken) {
    return false;
  }

  if (!hasConfiguredTrainingDays(workout) || workout.Duur_in_weken === 0) {
    return false;
  }

  const date = moment(datum).startOf('day');
  const programStart = moment(workout.Startdatum).startOf('day');
  const programEnd = programStart.clone().add(workout.Duur_in_weken * 7, 'days');

  if (date.isBefore(programStart, 'day') || !date.isBefore(programEnd, 'day')) {
    return false;
  }

  const dayMap = [
    workout.Zondag,
    workout.Maandag,
    workout.Dinsdag,
    workout.Woensdag,
    workout.Donderdag,
    workout.Vrijdag,
    workout.Zaterdag,
  ];
  return isTrainingDayFlag(dayMap[date.day()]);
}

export function enrichWorkoutDayStats(workout: Workout, oefeningen: WorkoutOefeningKort[]): Workout {
  const total = oefeningen?.length ?? 0;
  const incomplete = (oefeningen ?? []).filter((o) => !o.Voltooid).length;

  return {
    ...workout,
    AantalOefeningen: total,
    Onvoltooid: incomplete,
  };
}

export function getPlannedTrainingDatesInWeek(workouts: Workout[], referenceDate: Date): Date[] {
  const weekStart = moment(referenceDate).startOf('isoWeek');
  const weekEnd = moment(referenceDate).endOf('isoWeek');
  const dates: Date[] = [];
  const cursor = weekStart.clone();

  while (!cursor.isAfter(weekEnd, 'day')) {
    if (workouts.some((workout) => isWorkoutPlannedOnDate(workout, cursor.toDate()))) {
      dates.push(cursor.toDate());
    }
    cursor.add(1, 'day');
  }

  return dates;
}
